// routes.ts
import { lazy } from 'react';

// Types for our data structures
export type SubCategory = {
  id: string;
  name: string;
  path: string;
};

export type Category = {
  id: string;
  name: string;
  icon: string;
  subCategories: SubCategory[];
  isOpen?: boolean;
};

// Define all categories and subcategories
export const categories: Category[] = [
  {
    id: 'json',
    name: 'JSON',
    icon: '',
    isOpen: false,
    subCategories: [
      { id: 'json-to-sql', name: 'JSON to SQL', path: '/json-to-sql' },
      { id: 'json-to-excel', name: 'JSON to Excel', path: '/json-to-excel' },
      { id: 'json-to-csv', name: 'JSON to CSV', path: '/json-to-csv' },
      { id: 'json-to-xml', name: 'JSON to XML', path: '/json-to-xml' },
      { id: 'json-to-pdf', name: 'JSON to PDF', path: '/json-to-pdf' },
      { id: 'json-to-html', name: 'JSON to HTML', path: '/json-to-html' },
      { id: 'json-to-yaml', name: 'JSON to YAML', path: '/json-to-yaml' },
      { id: 'json-to-markdown', name: 'JSON to Markdown', path: '/json-to-markdown' },
      { id: 'json-to-typescript', name: 'JSON to TypeScript', path: '/json-to-typescript' },
      { id: 'json-to-csharp', name: 'JSON to C#', path: '/json-to-csharp' },
      { id: 'json-to-java', name: 'JSON to Java', path: '/json-to-java' },
      { id: 'json-to-python', name: 'JSON to Python', path: '/json-to-python' },
      { id: 'json-to-go', name: 'JSON to Go', path: '/json-to-go' },
      { id: 'json-to-swift', name: 'JSON to Swift', path: '/json-to-swift' },
      { id: 'json-to-kotlin', name: 'JSON to Kotlin', path: '/json-to-kotlin' },
      { id: 'json-to-protobuf', name: 'JSON to Protobuf', path: '/json-to-protobuf' },
      { id: 'json-to-graphql', name: 'JSON to GraphQL', path: '/json-to-graphql' },
      { id: 'json-to-bson', name: 'JSON to BSON', path: '/json-to-bson' },
      { id: 'json-to-avro', name: 'JSON to Avro', path: '/json-to-avro' },
      { id: 'json-to-schema', name: 'JSON to Schema', path: '/json-to-schema' },
      { id: 'json-to-parquet', name: 'JSON to Parquet', path: '/json-to-parquet' },
      { id: 'json-to-toml', name: 'JSON to TOML', path: '/json-to-toml' },
      { id: 'json-to-rss', name: 'JSON to RSS', path: '/json-to-rss' },
      { id: 'json-to-ical', name: 'JSON to iCal', path: '/json-to-ical' },
      { id: 'json-to-svg', name: 'JSON to SVG', path: '/json-to-svg' },
      { id: 'json-to-chart', name: 'JSON to Chart', path: '/json-to-chart' },
      { id: 'json-to-latex', name: 'JSON to LaTeX', path: '/json-to-latex' },
      { id: 'json-to-mongodb', name: 'JSON to MongoDB Query', path: '/json-to-mongodb' },
    ],
  },
  {
    id: 'sql',
    name: 'SQL',
    icon: 'SQL',
    isOpen: false,
    subCategories: [
      { id: 'sql-to-json', name: 'SQL to JSON', path: '/sql-to-json' },
      { id: 'sql-to-csv', name: 'SQL to CSV', path: '/sql-to-csv' },
      { id: 'sql-to-excel', name: 'SQL to Excel', path: '/sql-to-excel' },
      { id: 'sql-to-xml', name: 'SQL to XML', path: '/sql-to-xml' },
      { id: 'sql-to-yaml', name: 'SQL to YAML', path: '/sql-to-yaml' },
      { id: 'sql-to-html', name: 'SQL to HTML Table', path: '/sql-to-html' },
      { id: 'sql-to-markdown', name: 'SQL to Markdown Table', path: '/sql-to-markdown' },
      { id: 'sql-to-text', name: 'SQL to Text Table', path: '/sql-to-text' },
      { id: 'sql-to-sqlite', name: 'SQL to SQLite', path: '/sql-to-sqlite' },
      { id: 'sql-to-mongodb', name: 'SQL to MongoDB (NoSQL)', path: '/sql-to-mongodb' },
      { id: 'sql-to-graphql', name: 'SQL to GraphQL Schema', path: '/sql-to-graphql' },
      { id: 'sql-to-php-array', name: 'SQL to PHP Array', path: '/sql-to-php-array' },
      { id: 'sql-to-python', name: 'SQL to Python Dictionary', path: '/sql-to-python' },
      { id: 'sql-to-javascript', name: 'SQL to JavaScript Object', path: '/sql-to-javascript' },
      { id: 'sql-to-api', name: 'SQL to API JSON Response', path: '/sql-to-api' },
      // { id: 'sql-to-insert', name: 'SQL to Insert Statements', path: '/sql-to-insert' },
      { id: 'sql-to-er-diagram', name: 'SQL to ER Diagram', path: '/sql-to-er-diagram' },
    ]
  },
  {
    id: 'csv',
    name: 'CSV',
    icon: '📊',
    isOpen: false,
    subCategories: [
      { id: 'csv-to-json', name: 'CSV to JSON', path: '/csv-to-json' },
      { id: 'csv-to-sql', name: 'CSV to SQL', path: '/csv-to-sql' },
      { id: 'csv-to-excel', name: 'CSV to Excel', path: '/csv-to-excel' },
      { id: 'csv-to-xml', name: 'CSV to XML', path: '/csv-to-xml' },
      { id: 'csv-to-yaml', name: 'CSV to YAML', path: '/csv-to-yaml' },
      { id: 'csv-to-html', name: 'CSV to HTML Table', path: '/csv-to-html' },
      { id: 'csv-to-markdown', name: 'CSV to Markdown Table', path: '/csv-to-markdown' },
      { id: 'csv-to-text', name: 'CSV to Text Table', path: '/csv-to-text' },
      { id: 'csv-to-jsonl', name: 'CSV to JSON Lines', path: '/csv-to-jsonl' },
      { id: 'csv-to-graphql', name: 'CSV to GraphQL Schema', path: '/csv-to-graphql' },
    ]
  },
  {
    id: 'xml',
    name: 'XML',
    icon: '</>',
    isOpen: false,
    subCategories: [
      { id: 'xml-to-json', name: 'XML to JSON', path: '/xml-to-json' },
      { id: 'xml-to-csv', name: 'XML to CSV', path: '/xml-to-csv' },
      { id: 'xml-to-html', name: 'XML to HTML', path: '/xml-to-html' },
      { id: 'xml-to-yaml', name: 'XML to YAML', path: '/xml-to-yaml' },
      { id: 'xml-to-sql', name: 'XML to SQL', path: '/xml-to-sql' },
      { id: 'xml-to-markdown', name: 'XML to Markdown', path: '/xml-to-markdown' },
      { id: 'xml-to-xsd', name: 'XML to XSD', path: '/xml-to-xsd' },
    ]
  },
  {
    id: 'yaml',
    name: 'YAML',
    icon: 'YML',
    isOpen: false,
    subCategories: [
      { id: 'yaml-to-json', name: 'YAML to JSON', path: '/yaml-to-json' },
      { id: 'yaml-to-xml', name: 'YAML to XML', path: '/yaml-to-xml' },
      { id: 'yaml-to-csv', name: 'YAML to CSV', path: '/yaml-to-csv' },
    ]
  },
  {
    id: 'pdf',
    name: 'PDF',
    icon: '📄',
    isOpen: false,
    subCategories: [
      { id: 'pdf-to-json', name: 'PDF to JSON', path: '/pdf-to-json' },
      { id: 'pdf-to-xml', name: 'PDF to XML', path: '/pdf-to-xml' },
      { id: 'pdf-to-excel', name: 'PDF to Excel', path: '/pdf-to-excel' },
      {id: 'pdf-to-word', name: 'PDF to Word', path: '/pdf-to-word'},
      { id: 'pdf-to-text', name: 'PDF to Text', path: '/pdf-to-text' },
    ]
  }
];


// Helper function to find category and subcategory by IDs
export const findCategoryAndSubcategory = (categoryId?: string, subcategoryId?: string) => {
  const category = categoryId ? categories?.find(c => c.id === categoryId) : null;
  let subcategory = null;

  if (category && subcategoryId) {
    subcategory = category.subCategories.find(s => s.id === subcategoryId);
  }

  return { category, subcategory };
};

// For use in React Router config
export const getConverterRoutes = () => {
  const routes = [];

  // Add the base converter route
  routes.push({
    path: '/convert',
    element: lazy(() => import('../pages/frontend/ConvertData')),
  });

  // Add routes for each category
  categories?.forEach(category => {
    routes.push({
      path: `/convert/${category.id}`,
      element: lazy(() => import('../pages/frontend/ConvertData'))
    });

    // Add routes for each subcategory
    category.subCategories.forEach(subcategory => {
      routes.push({
        path: subcategory.path,
        element: lazy(() => import('../pages/frontend/ConvertData'))
      });
    });
  });

  return routes;
};

// Export all paths as an array for easy reference
export const getAllPaths = () => {
  const paths: string[] = ['/convert'];

  categories?.forEach(category => {
    paths.push(`/convert/${category.id}`);

    category.subCategories.forEach(subcategory => {
      paths.push(subcategory.path);
    });
  });

  return paths;
};